// npm
import React, { Component } from "react";
import { connect } from "react-redux";

// material-ui
import withStyles from "@material-ui/core/styles/withStyles";
import Divider from "@material-ui/core/Divider";

// react
import { Align } from "components/layout/align";
import CalculatorButtonRow from "./calculator-button-row";
import { CalculatorDisplay } from "./calculator-display";
import {
    isSEB,
    removeCalculatorSymbols,
    isValidPercentage,
    processPercentage,
    isValidSquareRoot,
    processSquareRoot,
    processPower,
    processInverse,
} from "./calculator-helper";

import check from "check-types";

import {
    isOperand,
    isOperator,
    getResult,
    plusMinusValue,
    spaceOutResult,
    decimalPointAllowed,
    operatorAllowed,
} from "./calculator-helper";

import { getSettingsData } from "redux/reducers/selectors";
import { getClientSettingsData } from "redux/reducers/settings/selectors";
import {
    getCalculatorPercentageButton,
    getCalculatorSquareButton,
    getWorkingsCalculatorScientific,
} from "redux/reducers/settings/client/selectors";

// constants
import {
    CALCULATOR_BUTTONS,
    CALCULATOR_SCIENTIFIC_BUTTONS,
    CALCULATOR_SCIENTIFIC_INSERT_TOKENS,
    CALCULATOR_SCIENTIFIC_RESET_TOKENS,
    CALCULATOR_EVALUATION,
} from "./constants";

// Calculator (not connected to styles)
// ----------------------------------------------------------------------------

class Calculator extends Component {
    constructor(props) {
        super(props);
        this.state = {
            value: "",
        };

        this.justProcessedAnswer = false;
        this.maxLength = -1;
        this.handleButtonClick = this.handleButtonClick.bind(this);
    }

    // scientific-mode buttons (sin/cos/log/sqrt/etc) only make sense evaluated by mathjs, so
    // scientific mode forces this regardless of the org's own CalculatorEvaluationMode setting
    getEvaluationType() {
        const { workingsCalculatorScientific, evaluationType } = this.props;
        return workingsCalculatorScientific ? CALCULATOR_EVALUATION.scientific : evaluationType;
    }

    handleButtonClick(_button) {
        const { value: _value } = this.state;
        const { workingsCalculatorScientific } = this.props;
        const evaluationType = this.getEvaluationType();

        let value = removeCalculatorSymbols(_value);
        const button = removeCalculatorSymbols(_button);

        // operatorAllowed() only recognises a digit as a valid place to put an operator - it
        // predates scientific tokens like 'pi'/'sin('/')' and would otherwise silently reject
        // an operator typed straight after one (e.g. "pi" then "/"). mathjs is the real
        // arbiter of a scientific expression's validity anyway (see '=' below), so skip this
        // hand-rolled check in scientific mode rather than trying to teach it every valid
        // trailing token.
        if (isOperand(button) && this.justProcessedAnswer) {
            value = button;
            this.justProcessedAnswer = false;
        } else if (CALCULATOR_SCIENTIFIC_RESET_TOKENS.includes(button) && this.justProcessedAnswer) {
            // a freshly-cleared expression has no unclosed '(' to match, so 'paren' always
            // resets to an opening bracket
            value = button === "paren" ? "(" : CALCULATOR_SCIENTIFIC_INSERT_TOKENS[button];
            this.justProcessedAnswer = false;
        } else if (
            isOperand(button) ||
            (isOperator(button) && (workingsCalculatorScientific || operatorAllowed(button, value)))
        ) {
            if (button !== "." || decimalPointAllowed(value)) {
                if (this.maxLength === -1 || value.length < this.maxLength) {
                    value += button;
                    this.justProcessedAnswer = false;
                }
            }
        } else if (button === "=") {
            const result = getResult(value, evaluationType);

            if (check.assigned(result)) {
                this.props.onResult(spaceOutResult("" + value, result, button));
                value = "" + result;
                this.justProcessedAnswer = true;
            }
        } else if (button === "C") {
            value = "";
            this.justProcessedAnswer = false;
        } else if (button === "<") {
            value = value.slice(0, -1);
            this.justProcessedAnswer = false;
        } else if (
            button === "@" &&
            (this.maxLength === -1 || value.length < this.maxLength)
        ) {
            value = plusMinusValue(value);
        } else if (button === "%" && isValidPercentage(value)) {
            const result = processPercentage(value);
            if (result) {
                this.props.onResult(spaceOutResult("" + value, result, button));
                value = "" + result;
                this.justProcessedAnswer = true;
            }
        } else if (button === "}" && isValidSquareRoot(value)) {
            const squareRootValue = processSquareRoot(value, evaluationType);

            if (check.assigned(squareRootValue)) {
                this.props.onResult(
                    spaceOutResult("" + value, squareRootValue, button)
                );
                value = "" + squareRootValue;
                this.justProcessedAnswer = true;
            }
        } else if (button === "sq" && value) {
            const squared = processPower(value, evaluationType, 2);

            if (check.assigned(squared)) {
                this.props.onResult(spaceOutResult("" + value, squared, button));
                value = "" + squared;
                this.justProcessedAnswer = true;
            }
        } else if (button === "inv" && value) {
            const inverted = processInverse(value, evaluationType);

            if (check.assigned(inverted)) {
                this.props.onResult(spaceOutResult("" + value, inverted, button));
                value = "" + inverted;
                this.justProcessedAnswer = true;
            }
        } else if (button === "paren") {
            // one smart bracket key, like a phone calculator: inserts '(' unless there's
            // already an unclosed one, in which case it closes it instead
            if (this.maxLength === -1 || value.length < this.maxLength) {
                const openCount = (value.match(/\(/g) || []).length;
                const closeCount = (value.match(/\)/g) || []).length;
                value += openCount > closeCount ? ")" : "(";
                this.justProcessedAnswer = false;
            }
        } else if (CALCULATOR_SCIENTIFIC_INSERT_TOKENS[button]) {
            if (this.maxLength === -1 || value.length < this.maxLength) {
                value += CALCULATOR_SCIENTIFIC_INSERT_TOKENS[button];
                this.justProcessedAnswer = false;
            }
        }

        this.setState({ value });
    }

    getCalculatorButtons() {
        const { classes, workingsCalculatorScientific } = this.props;

        return (
            <React.Fragment>
                {workingsCalculatorScientific ? this.getScientificButtons() : this.getExtraButtons()}
                {workingsCalculatorScientific && <Divider className={classes.divider}/>}
                {CALCULATOR_BUTTONS.map((buttonRow, index) => (
                    <CalculatorButtonRow
                        key={index}
                        buttons={buttonRow}
                        onSelect={this.handleButtonClick}
                    />
                ))}
            </React.Fragment>
        );
    }

    getScientificButtons() {
        return CALCULATOR_SCIENTIFIC_BUTTONS.map((buttonRow, index) => (
            <CalculatorButtonRow
                key={`scientific-${index}`}
                buttons={buttonRow}
                onSelect={this.handleButtonClick}
            />
        ));
    }

    getExtraButtons() {
        const { calculatorPercentageButton, calculatorSquareButton } =
            this.props;

        if (calculatorPercentageButton || calculatorSquareButton) {
            let buttonRow = [];
            if (calculatorSquareButton) {
                buttonRow.unshift("}");
            }
            if (calculatorPercentageButton) {
                buttonRow.unshift("%");
            }
            const max=4-buttonRow.length;
            for (let i = 0; i < max; i++) {
                buttonRow.unshift("");
            }

            return (
                <CalculatorButtonRow
                    buttons={buttonRow}
                    onSelect={this.handleButtonClick}
                />
            );
        } else {
            return null;
        }
    }

    render() {
        const { classes } = this.props;
        const { value } = this.state;
        const displayProps = {
            value,
        };
        const className = `${classes.container} ${isSEB() && classes.seb}`;
        return (
            <div className={className}>
                <Align>
                    <div>
                        {/* <div>{navigator.userAgent}</div> */}
                        <CalculatorDisplay {...displayProps} />
                        {this.getCalculatorButtons(this.handleButtonClick)}
                    </div>
                </Align>
            </div>
        );
    }
}

// Calculator (connected to styles)
// ----------------------------------------------------------------------------

const styles = ({ palette, spacing }) => ({
    container: {
        borderWidth: 1,
        boxSizing: "border-box",
        borderStyle: "solid",
        borderColor: palette.background.contrastText,
        borderRadius: 8,
        flexShrink: 0,
        margin: spacing.unit,
        marginTop: 0,
    },
    seb: {
        marginBottom: spacing.unit * 8,
    },
    divider: {
        margin: `${spacing.unit}px ${spacing.unit}px`,
    },
});

Calculator = withStyles(styles)(Calculator);

const mapStoreToProps = (store) => {
    const settingsData = getSettingsData(store);
    const clientSettingsData = getClientSettingsData(settingsData);

    const calculatorPercentageButton =
        getCalculatorPercentageButton(clientSettingsData);
    const calculatorSquareButton =
        getCalculatorSquareButton(clientSettingsData);
    const workingsCalculatorScientific =
        getWorkingsCalculatorScientific(clientSettingsData);

    return {
        calculatorPercentageButton,
        calculatorSquareButton,
        workingsCalculatorScientific,
    };
};

Calculator = connect(mapStoreToProps)(Calculator);

// EXPORT
// ----------------------------------------------------------------------------
export { Calculator };
